import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	Box,
	Button,
	ButtonProps,
	Flex,
	FormControl,
	IconButton,
	Input,
	PlusOutline,
	rem,
	Spinner,
	Stack,
	Text,
	TrashOutline,
	useToast,
	VideoCameraOutline,
} from '@bonusly/design-system';

import { FormLabel } from '../../form/form-label';
import { useUploadAnnouncementVideo } from '../hooks/use-upload-announcement-video';
import { FormValues, Video } from '../types/announcements';

const ACCEPTED_FORMATS = 'video/mp4,video/webm,video/quicktime,video/x-m4v';
// 300MB matches the backend VideoUploader limit
const MAX_FILE_SIZE = 300 * 1048576;

interface VideoUploadFieldProps {
	disabled?: boolean;
	onVideoAttached?: () => void;
	onVideoRemoved?: () => void;
	setRemoveVideo?: (removeVideo: boolean) => void;
	hadInitialVideo?: boolean;
	initialVideoId?: string | null;
	onUploadStateChange?: (isUploading: boolean) => void;
}

export function VideoUploadField({
	disabled = false,
	onVideoAttached,
	onVideoRemoved,
	setRemoveVideo,
	hadInitialVideo = false,
	initialVideoId = null,
	onUploadStateChange,
}: VideoUploadFieldProps) {
	const { t } = useTranslation('announcements', { keyPrefix: 'modal' });
	const { track } = useAnalytics();
	const toast = useToast();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const { setValue, watch } = useFormContext<FormValues>();
	const video = watch('video');

	const { uploadVideo, cancelUpload, discardVideo, isUploading } = useUploadAnnouncementVideo({
		onSuccess: (uploaded: Video) => {
			setValue('video', uploaded, { shouldValidate: true, shouldDirty: true });
			setRemoveVideo?.(false);
			track(AnalyticsEvent.Announcements.AttachVideoSuccess);
			onVideoAttached?.();
		},
	});

	// Lift the upload state so the parent modal can disable the image field
	// while a video is being uploaded — otherwise both attachments can populate
	// in parallel and the submit silently drops one.
	useEffect(() => {
		onUploadStateChange?.(isUploading);
	}, [isUploading, onUploadStateChange]);

	// Refs kept in sync during render so the unmount cleanup effect (which runs
	// with [] deps) can read the latest values. Capturing `video` directly in
	// the cleanup closure would freeze it to the initial render — null in both
	// Make and Edit modals, since the Edit modal populates it via setValue
	// after mount. Sync-on-render is the React-documented pattern for bridging
	// rendered state into a cleanup closure; updating via useEffect instead
	// would race with an unmount that happens before the update commits.
	const currentVideoIdRef = useRef<string | null>(null);
	const initialVideoIdRef = useRef<string | null>(initialVideoId);
	currentVideoIdRef.current = video?.video_id ?? null;
	initialVideoIdRef.current = initialVideoId;

	// On unmount (modal close), tell the server to discard any video the user
	// uploaded but never attached to a saved announcement. The daily orphan
	// sweeper would catch it eventually, but this keeps S3 cleaner.
	useEffect(() => {
		return () => {
			cancelUpload();
			const currentVideoId = currentVideoIdRef.current;
			if (currentVideoId && currentVideoId !== initialVideoIdRef.current) {
				discardVideo(currentVideoId);
			}
		};
		// We intentionally only run this on unmount; refs keep the ids fresh.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClickOrKeyDown = useCallback(
		(e: KeyboardEvent | MouseEvent) => {
			if (disabled || isUploading) return;
			if (e.type === 'click' || (e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
				fileInputRef.current?.click();
			}
		},
		[disabled, isUploading]
	);

	const handleFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;

			if (file.size > MAX_FILE_SIZE) {
				e.target.value = '';
				toast({
					title: t('video_upload_too_big'),
					status: 'error',
					position: 'bottom-right',
				});
				return;
			}

			track(AnalyticsEvent.Announcements.AttachVideoRequestSubmitted);
			uploadVideo(file).finally(() => {
				if (fileInputRef.current) fileInputRef.current.value = '';
			});
		},
		[t, toast, track, uploadVideo]
	);

	const handleRemoveVideo = useCallback(() => {
		const currentVideoId = video?.video_id;
		if (isUploading) {
			cancelUpload();
		}
		// If the video being removed was uploaded in this session (not the one
		// the announcement was opened with), discard it on the server so it
		// doesn't sit around as an orphan.
		if (currentVideoId && currentVideoId !== initialVideoId) {
			discardVideo(currentVideoId);
		}
		setValue('video', null, { shouldValidate: true, shouldDirty: true });
		if (hadInitialVideo) {
			setRemoveVideo?.(true);
		}
		track(AnalyticsEvent.Announcements.RemoveVideoButtonClicked);
		onVideoRemoved?.();
	}, [video?.video_id, isUploading, cancelUpload, initialVideoId, discardVideo, setValue, hadInitialVideo, setRemoveVideo, track, onVideoRemoved]);

	const renderUploading = () => (
		<Flex
			h="fit-content"
			p={3}
			w="100%"
			alignItems="center"
			justifyContent="space-between"
			borderColor="border.input"
			borderRadius="md"
			borderWidth={rem(1)}
			backgroundColor="bg.surface"
			color="text.default"
		>
			<Flex alignItems="center" gap={3} overflow="hidden">
				<Spinner size="sm" />
				<Text fontSize={50} fontWeight="medium" noOfLines={1}>
					{t('video_uploading')}
				</Text>
			</Flex>
			<Button colorScheme="secondary" data-testid="cancel-video-upload" onClick={handleRemoveVideo} size="sm" variant="ghost">
				{t('video_cancel_upload')}
			</Button>
		</Flex>
	);

	const renderAttached = (attached: Video) => {
		const previewSrc = attached.video_poster_url || attached.video_thumbnail_url || undefined;
		return (
			<Flex
				h="fit-content"
				p={3}
				w="100%"
				alignItems="center"
				justifyContent="space-between"
				borderColor="border.input"
				borderRadius="md"
				borderWidth={rem(1)}
				backgroundColor="bg.surface"
				color="text.default"
			>
				<Flex alignItems="center" gap={3} overflow="hidden">
					<Box flexShrink={0} w={rem(64)} h={rem(64)} borderRadius="sm" overflow="hidden" backgroundColor="bg.subtle" position="relative">
						{previewSrc ? (
							<Box as="img" src={previewSrc} alt="" w="100%" h="100%" objectFit="cover" />
						) : (
							<Flex w="100%" h="100%" alignItems="center" justifyContent="center" color="text.softer">
								<VideoCameraOutline size="md" />
							</Flex>
						)}
					</Box>
					<Stack spacing={0} overflow="hidden">
						<Text fontSize={50} fontWeight="medium" noOfLines={1}>
							{t('video_attached_label')}
						</Text>
						<Text fontSize={50} color="text.softer" noOfLines={2}>
							{t('video_processing_helper')}
						</Text>
					</Stack>
				</Flex>
				<IconButton
					aria-label={t('delete_video')}
					data-testid="delete-video"
					onClick={handleRemoveVideo}
					colorScheme="secondary"
					variant="ghost"
					flexShrink={0}
					_hover={{ bgColor: 'secondary.bgAlpha' }}
				>
					<TrashOutline />
				</IconButton>
			</Flex>
		);
	};

	const renderEmpty = () => (
		<>
			<StyledButton
				aria-label={t('video_upload_action')}
				aria-labelledby="video-attachment"
				onClick={handleClickOrKeyDown}
				onKeyDown={handleClickOrKeyDown}
				isDisabled={disabled}
			>
				<Flex alignItems="center">
					<PlusOutline color="text.default" size="sm" />
					<Stack ml={4} spacing={0} textAlign="left">
						<Text color="text.default" fontSize={50} fontWeight="medium">
							{t('video_upload_action')}
						</Text>
						<Text color="text.softer" fontSize={50} fontWeight="normal">
							{t('video_upload_description')}
						</Text>
					</Stack>
				</Flex>
			</StyledButton>
			<Input
				ref={fileInputRef}
				id="video-uploader"
				data-testid="video-uploader"
				accept={ACCEPTED_FORMATS}
				display="none"
				type="file"
				onChange={handleFileChange}
			/>
		</>
	);

	let content;
	if (isUploading) {
		content = renderUploading();
	} else if (video) {
		content = renderAttached(video);
	} else {
		content = renderEmpty();
	}

	return (
		<FormControl data-testid="video-upload-field">
			<FormLabel fontSize={50} lineHeight={5} id="video-attachment" isOptional>
				{t('video_upload_label')}
			</FormLabel>
			{content}
		</FormControl>
	);
}

const StyledButton = (props: ButtonProps) => (
	<Button
		h="fit-content"
		p={4}
		w="100%"
		justifyContent="left"
		borderColor="border.input"
		borderRadius="md"
		borderWidth={rem(1)}
		backgroundColor="bg.surface"
		color="text.default"
		_active={{
			borderColor: 'border.input',
			borderRadius: 'md',
			borderWidth: rem(1),
			backgroundColor: 'bg.surface',
			color: 'text.default',
		}}
		_disabled={{
			cursor: 'not-allowed',
			opacity: 0.6,
		}}
		_hover={{
			borderColor: 'border.input',
			borderRadius: 'md',
			borderWidth: rem(1),
			backgroundColor: 'bg.surface',
			color: 'text.default',
		}}
		{...props}
	/>
);
